import apiService, { API_BASE_URL } from './apiService';
import websocketService from './websocketService';

class TradingViewDatafeed {
  constructor(marketsData, duelData = null, tradeData = null) {
    this.marketsData = marketsData;
    this.duelData = duelData;
    this.tradeData = tradeData;
    this.subscriptions = new Map();
    this.lastBars = new Map();
    this.onRealtimeCallback = null;
    this.lastUpdateTimes = new Map(); // Track last update time per symbol for throttling
    this.pendingUpdates = new Map(); // Queue pending updates
    this.lastGetBarsCall = new Map(); // Track last getBars call per symbol+resolution
  }

  /**
   * Validate bar data
   */
  isValidBar(bar) {
    if (!bar || typeof bar !== 'object') {
      return false;
    }

    const { time, open, high, low, close, volume } = bar;

    // Check if all required fields exist and are numbers
    if (typeof time !== 'number' || typeof open !== 'number' || typeof high !== 'number' ||
        typeof low !== 'number' || typeof close !== 'number' || typeof volume !== 'number') {
      return false;
    }

    // Check for NaN values
    if (isNaN(time) || isNaN(open) || isNaN(high) || isNaN(low) || isNaN(close) || isNaN(volume)) {
      return false;
    }

    // Check for negative or zero values where they shouldn't be
    if (time <= 0 || open <= 0 || high <= 0 || low <= 0 || close <= 0 || volume < 0) {
      return false;
    }

    // Check OHLC logic (high >= open, high >= close, low <= open, low <= close)
    if (high < Math.max(open, close) || low > Math.min(open, close)) {
      return false;
    }

    // Check reasonable timestamp (not in future, not too old - within last 24 hours for safety)
    const now = Date.now();
    const oneDayAgo = now - (24 * 60 * 60 * 1000);
    if (time > now + 60000 || time < oneDayAgo) { // Allow 1 minute in future for clock sync
      return false;
    }

    return true;
  }

  /**
   * Simple throttle updates to once per second per symbol
   */
  throttleUpdate(symbol, updateCallback) {
    const now = Date.now();
    const lastUpdate = this.lastUpdateTimes.get(symbol) || 0;

    if (now - lastUpdate >= 1000) { // 1 second throttle
      updateCallback();
      this.lastUpdateTimes.set(symbol, now);
    } else {
      // Replace any pending update
      const existingTimeout = this.pendingUpdates.get(symbol);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      const timeoutId = setTimeout(() => {
        updateCallback();
        this.lastUpdateTimes.set(symbol, Date.now());
        this.pendingUpdates.delete(symbol);
      }, 1000 - (now - lastUpdate));

      this.pendingUpdates.set(symbol, timeoutId);
    }
  }

  /**
   * Called when the datafeed is ready
   */
  onReady(callback) {
    setTimeout(() => {
      callback({
        exchanges: [],
        symbols_types: [],
        supported_resolutions: ['1', '5', '15', '60', '240', '1D'],
        supports_marks: true,
        supports_timescale_marks: true,
        supports_time: true,
      });
    }, 0);
  }

  /**
   * Resolve symbol information
   */
  resolveSymbol(symbolName, onResolve, onError) {
    // TradingView requires resolveSymbol to be asynchronous
    setTimeout(() => {
      const market = this.marketsData.find(m => m.symbol === symbolName);
      if (!market) {
        onError('Symbol not found');
        return;
      }

      const symbolInfo = {
        name: market.symbol,
        description: market.symbol,
        type: 'crypto',
        session: '24x7',
        timezone: 'UTC',
        ticker: market.symbol,
        exchange: 'LUMOS',
        minmov: 1,
        pricescale: Math.pow(10, market.supported_price_decimals || 2),
        has_intraday: true,
        has_daily: true,
        has_weekly_and_monthly: true,
        supported_resolutions: ['1', '5', '15', '60', '240', '1D'],
        volume_precision: 0,
        data_status: 'streaming',
      };

      onResolve(symbolInfo);
    }, 0);
  }

  /**
   * Get historical bars with improved error handling and throttling
   */
  getBars(symbolInfo, resolution, periodParams, onHistoryCallback, onError) {
    const { countBack } = periodParams;

    // Convert resolution to API format
    const resolutionMap = { '1': '1m', '5': '5m', '15': '15m', '60': '1h', '240': '4h', '1D': '1d' };
    const apiResolution = resolutionMap[resolution] || '1m';

    // Find market
    const market = this.marketsData.find(m => m.symbol === symbolInfo.name);
    if (!market) {
      onError('Market not found');
      return;
    }

    // Simple throttling - one call per symbol+resolution per second
    const requestKey = `${symbolInfo.name}_${apiResolution}`;
    const now = Date.now();
    const lastCall = this.lastGetBarsCall.get(requestKey);

    if (lastCall && now - lastCall < 1000) {
      console.log(`getBars throttled for ${requestKey}`);
      onHistoryCallback([], { noData: true });
      return;
    }

    this.lastGetBarsCall.set(requestKey, now);

    console.log(`Fetching bars for ${symbolInfo.name} (${apiResolution})`);

    apiService.getCandlesticks(market.market_id, apiResolution, countBack || 100)
      .then(data => {
        if (!Array.isArray(data)) {
          onError('Invalid data format');
          return;
        }

        const bars = data
          .map(candle => ({
            time: +candle.timestamp,
            open: +candle.open,
            high: +candle.high,
            low: +candle.low,
            close: +candle.close,
            volume: +(candle.volume1 || 0)
          }))
          .filter(bar => this.isValidBar(bar))
          .sort((a, b) => a.time - b.time);

        if (bars.length > 0) {
          this.lastBars.set(symbolInfo.name, bars[bars.length - 1]);
        }

        onHistoryCallback(bars, { noData: bars.length === 0 });
      })
      .catch(error => {
        console.error('Error fetching bars:', error);
        onError('Failed to load historical data');
      });
  }

  /**
   * Subscribe to real-time bars
   */
  subscribeBars(symbolInfo, resolution, onRealtimeCallback, subscriberUID) {
    this.subscriptions.set(subscriberUID, {
      symbol: symbolInfo.name,
      resolution,
      callback: onRealtimeCallback
    });

    // Connect to WebSocket and subscribe to market trades
    if (!websocketService.getStatus().isConnected) {
      websocketService.connect();
    }

    const market = this.marketsData.find(m => m.symbol === symbolInfo.name);
    if (market) {
      websocketService.subscribeToTrades(market.market_id);
    }

    // Set up WebSocket handler once
    this.setupWebSocketHandler();
  }

  /**
   * Unsubscribe from real-time bars
   */
  unsubscribeBars(subscriberUID) {
    const subscription = this.subscriptions.get(subscriberUID);
    if (subscription) {
      const existingTimeout = this.pendingUpdates.get(subscription.symbol);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
        this.pendingUpdates.delete(subscription.symbol);
      }
    }
    this.subscriptions.delete(subscriberUID);
  }

  /**
   * Set up WebSocket message handling
   */
  setupWebSocketHandler() {
    if (this.websocketHandlerSet) return;

    websocketService.on('message', (data) => {
      if (data.type === 'update/trade' && Array.isArray(data.trades)) {
        this.handleRealtimeTrades(data.trades, data.channel);
      }
    });

    this.websocketHandlerSet = true;
  }

  /**
   * Validate trade data
   */
  isValidTrade(trade) {
    if (!trade || typeof trade !== 'object') {
      return false;
    }

    const { timestamp, price, size } = trade;

    // Check required fields exist
    if (timestamp === undefined || timestamp === null || price === undefined || price === null) {
      return false;
    }

    // Parse numeric values (API sends strings)
    const parsedTimestamp = typeof timestamp === 'string' ? parseFloat(timestamp) : timestamp;
    const parsedPrice = typeof price === 'string' ? parseFloat(price) : price;
    const parsedSize = size !== undefined ? (typeof size === 'string' ? parseFloat(size) : size) : undefined;

    // Check for NaN after parsing
    if (isNaN(parsedTimestamp) || isNaN(parsedPrice)) {
      return false;
    }

    // Size is optional, but if present must be valid
    if (parsedSize !== undefined && (isNaN(parsedSize) || parsedSize < 0)) {
      return false;
    }

    // Check reasonable values
    if (parsedTimestamp <= 0 || parsedPrice <= 0) {
      return false;
    }

    // Check reasonable timestamp (handle both seconds and milliseconds)
    const now = Date.now();
    let timestampMs = parsedTimestamp;

    // If timestamp is in seconds (Unix timestamp), convert to milliseconds
    if (timestampMs < 1e10) { // Less than 10 billion means it's likely in seconds
      timestampMs = timestampMs * 1000;
    }

    const oneDayAgo = now - (24 * 60 * 60 * 1000);
    if (timestampMs > now + 60000 || timestampMs < oneDayAgo) {
      return false;
    }

    return true;
  }

  /**
   * Handle real-time trade updates
   */
  handleRealtimeTrades(trades, channel) {
    const marketIdMatch = channel.match(/trade:(\d+)/);
    if (!marketIdMatch) return;

    const marketId = +marketIdMatch[1];
    const market = this.marketsData.find(m => m.market_id === marketId);
    if (!market) return;

    const validTrades = trades.filter(trade => this.isValidTrade(trade));
    if (validTrades.length === 0) return;

    // Process for each subscription of this market
    for (const subscription of this.subscriptions.values()) {
      if (subscription.symbol === market.symbol) {
        this.processTradesForSubscription(validTrades, subscription);
      }
    }
  }

  /**
   * Process trades for a subscription
   */
  processTradesForSubscription(trades, subscription) {
    const resolutionSeconds = this.resolutionToSeconds(subscription.resolution);
    const lastBar = this.lastBars.get(subscription.symbol);
    const candleGroups = new Map();

    // Group trades by candle
    for (const trade of trades) {
      let tradeTimeMs = +trade.timestamp;
      if (tradeTimeMs < 1e10) tradeTimeMs *= 1000; // Convert seconds to ms

      const candleTime = Math.floor(tradeTimeMs / (resolutionSeconds * 1000)) * resolutionSeconds * 1000;

      if (!candleGroups.has(candleTime)) {
        candleGroups.set(candleTime, {
          time: candleTime,
          open: null,
          high: -Infinity,
          low: Infinity,
          close: null,
          volume: 0
        });
      }

      const candle = candleGroups.get(candleTime);
      const price = +trade.price;
      const volume = +(trade.size || 0);

      // Update OHLCV
      if (candle.open === null) candle.open = price;
      candle.high = Math.max(candle.high, price);
      candle.low = Math.min(candle.low, price);
      candle.close = price;
      candle.volume += volume;
    }

    // Generate bars and throttle updates
    const updates = [];
    for (const candle of candleGroups.values()) {
      let bar = null;

      if (!lastBar || candle.time > lastBar.time) {
        // New candle
        bar = { time: candle.time, open: candle.open, high: candle.high, low: candle.low, close: candle.close, volume: candle.volume };
        this.lastBars.set(subscription.symbol, bar);
      } else if (candle.time === lastBar.time) {
        // Update existing candle
        bar = {
          time: candle.time,
          open: lastBar.open,
          high: Math.max(lastBar.high, candle.high),
          low: Math.min(lastBar.low, candle.low),
          close: candle.close,
          volume: lastBar.volume + candle.volume
        };
        this.lastBars.set(subscription.symbol, bar);
      }

      if (bar && this.isValidBar(bar)) {
        updates.push(bar);
      }
    }

    // Send throttled updates
    if (updates.length > 0) {
      this.throttleUpdate(subscription.symbol, () => {
        updates.forEach(bar => subscription.callback(bar));
      });
    }
  }

  /**
   * Convert resolution string to seconds
   */
  resolutionToSeconds(resolution) {
    const map = {
      '1': 60,
      '5': 300,
      '15': 900,
      '60': 3600,
      '240': 14400,
      '1D': 86400
    };
    return map[resolution] || 60;
  }

  /**
   * Get marks (for trade markers)
   */
  getMarks(symbolInfo, startDate, endDate, onDataCallback, resolution) {
    if (!this.duelData || !this.tradeData) {
      onDataCallback([]);
      return;
    }

    const marks = [];
    const market = this.marketsData.find(m => m.symbol === symbolInfo.name);
    if (!market) {
      onDataCallback([]);
      return;
    }

    // Filter trades for this market
    const marketTrades = this.tradeData.filter(trade =>
      parseInt(trade.market_id) === parseInt(market.market_id)
    );

    // Convert startDate and endDate to seconds if needed (TradingView uses milliseconds)
    const startTime = startDate;
    const endTime = endDate;

    marketTrades.forEach((trade, index) => {
      // Convert trade timestamp to milliseconds if it's in seconds
      let tradeTime = trade.created_at;
      if (typeof tradeTime === 'string') {
        tradeTime = new Date(tradeTime).getTime();
      }
      
      if (tradeTime > 1e10) { // If in milliseconds, convert to seconds
        tradeTime = tradeTime / 1000;
      }

      // Check if trade is within the visible range
      if (tradeTime >= startTime && tradeTime <= endTime) {
        const isChallenger = parseInt(trade.user_id) === parseInt(this.duelData.challengerUserId);
        // Use proxy URL to avoid CORS issues with Telegram avatars
        const avatarUrl = isChallenger ? this.duelData.challengerAvatarUrl : this.duelData.opponentAvatarUrl;
        const imageUrl = avatarUrl ? `${API_BASE_URL}/api/proxy/avatar?url=${encodeURIComponent(avatarUrl)}` : null;

        const color = trade.side === 'long' ? (trade.type === 'close_market' ? '#ef5350' : '#26a69a') : (trade.type === 'close_market' ? '#26a69a' : '#ef5350'); // Green for long, red for short
        const side = trade.type === 'close_market' ? (trade.side === 'long' ? 'short' : 'long') : trade.side;

        const mark = {
          id: `trade_${trade._id || index}`,
          time: tradeTime,
          color: {
            border: color,
            background: color,
          },
          text: `${isChallenger ? this.duelData.challengerUsername : this.duelData.opponentUsername} ${side} ${trade.filled_base_amount || ''} ${symbolInfo.name} @ $${trade.entry_price || ''}`,
          imageUrl: imageUrl,
          labelFontColor: 'white',
          minSize: 18
        };

        marks.push(mark);
      }
    });

    // Sort marks by time
    marks.sort((a, b) => a.time - b.time);

    console.log(`getMarks returning ${marks.length} marks for ${symbolInfo.name}`);
    onDataCallback(marks);
  }

  /**
   * Get timescale marks
   */
  getTimescaleMarks(symbolInfo, startDate, endDate, onDataCallback, resolution) {
    // This would be used for timescale marks
    onDataCallback([]);
  }

  /**
   * Update duel and trade data for marks
   */
  updateDuelData(duelData, tradeData) {
    this.duelData = duelData;
    // Filter out existing trades that have the same client_order_index as any new trade
    const filteredExistingTrades = this.tradeData.filter(existingTrade =>
      !tradeData.some(newTrade => newTrade.client_order_index === existingTrade.client_order_index)
    );
    // Combine filtered existing trades with new trades (new trades take precedence)
    this.tradeData = [...filteredExistingTrades, ...tradeData];
  }

  /**
   * Clean up resources when datafeed is destroyed
   */
  destroy() {
    try {
      // Clear all pending timeouts
      for (const [, timeoutId] of this.pendingUpdates) {
        clearTimeout(timeoutId);
      }

      // Clear all data structures
      this.pendingUpdates.clear();
      this.lastUpdateTimes.clear();
      this.subscriptions.clear();
      this.lastBars.clear();
      this.lastGetBarsCall.clear();

      // Reset state
      this.websocketHandlerSet = false;
      this.duelData = null;
      this.tradeData = null;

      console.log('Datafeed destroyed and cleaned up');
    } catch (error) {
      console.error('Error during datafeed cleanup:', error);
    }
  }
}

export default TradingViewDatafeed;
